import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, STAGES } from "@/lib/api";
import { PageHead, Panel } from "@/pages/vendor/Shell";

const tip = { contentStyle: { background: "#fff", border: "1px solid #E5E5E5", borderRadius: 4 }, cursor: { fill: "rgba(0,0,0,0.03)" } };
const FUNNEL_COLORS = ["#A3A3A3", "#F09E82", "#E97955", "#E05A33", "#1C1917"];

export default function Analytics() {
  const [d, setD] = useState<any>(null);
  useEffect(() => { api.get("/vendor/analytics").then((r) => setD(r.data)); }, []);
  if (!d) return <p className="text-muted-foreground">Loading...</p>;
  const stages = d.stages.map((s: any) => ({ ...s, label: STAGES.find((x) => x.key === s.stage)?.label, color: STAGES.find((x) => x.key === s.stage)?.color }));
  return (
    <div>
      <PageHead eyebrow="Performance" title="Analytics" />
      <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {d.funnel.slice(0, 3).map((f: any) => (
          <Panel key={f.step}><p className="text-xs uppercase tracking-wider text-muted-foreground">{f.step}</p><p className="mt-2 font-mono text-3xl">{f.value}</p></Panel>
        ))}
        <Panel data-testid="analytics-quote-rate"><p className="text-xs uppercase tracking-wider text-muted-foreground">Quote completion</p><p className="mt-2 font-mono text-3xl">{d.quote_rate}%</p></Panel>
      </div>
      <div className="grid gap-4 xl:grid-cols-2">
        <Panel data-testid="analytics-funnel">
          <h3 className="mb-4 font-semibold">Scan-to-booking funnel</h3>
          <div className="h-64">
            <ResponsiveContainer><BarChart data={d.funnel} layout="vertical" margin={{ left: 20 }}>
              <XAxis type="number" hide /><YAxis type="category" dataKey="step" width={110} stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} />
              <Tooltip {...tip} /><Bar dataKey="value" radius={[0, 6, 6, 0]}>{d.funnel.map((_: any, i: number) => <Cell key={i} fill={FUNNEL_COLORS[i]} />)}</Bar>
            </BarChart></ResponsiveContainer>
          </div>
          <p className="mt-2 font-mono text-xs text-muted-foreground">{d.repeat_scans} repeat scans logged without duplicate leads</p>
        </Panel>
        <Panel data-testid="analytics-hours">
          <h3 className="mb-4 font-semibold">Leads by hour (booth peak times)</h3>
          <div className="h-64">
            <ResponsiveContainer><BarChart data={d.hours}>
              <CartesianGrid stroke="#EEEEEE" vertical={false} /><XAxis dataKey="hour" stroke="#737373" fontSize={10} tickLine={false} />
              <YAxis allowDecimals={false} stroke="#737373" fontSize={11} width={24} tickLine={false} /><Tooltip {...tip} /><Bar dataKey="leads" fill="#E05A33" radius={[2, 2, 0, 0]} />
            </BarChart></ResponsiveContainer>
          </div>
        </Panel>
        <Panel data-testid="analytics-stages">
          <h3 className="mb-4 font-semibold">Pipeline by stage</h3>
          <div className="h-56">
            <ResponsiveContainer><BarChart data={stages}>
              <XAxis dataKey="label" stroke="#64748B" fontSize={10} tickLine={false} /><YAxis allowDecimals={false} stroke="#64748B" width={24} fontSize={11} tickLine={false} />
              <Tooltip {...tip} /><Bar dataKey="count" radius={[4, 4, 0, 0]}>{stages.map((s: any) => <Cell key={s.stage} fill={s.color} />)}</Bar>
            </BarChart></ResponsiveContainer>
          </div>
        </Panel>
        <Panel data-testid="analytics-events">
          <h3 className="mb-4 font-semibold">Events compared</h3>
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground"><tr><th className="pb-2">Event</th><th>Views</th><th>Leads</th><th>Quotes</th><th>Won</th></tr></thead>
            <tbody className="divide-y divide-border font-mono">
              {d.events.map((e: any) => <tr key={e.name}><td className="py-2 font-sans font-medium">{e.name}</td><td>{e.views}</td><td>{e.leads}</td><td>{e.quotes}</td><td>{e.won}</td></tr>)}
            </tbody>
          </table>
        </Panel>
        <Panel className="xl:col-span-2" data-testid="analytics-staff">
          <h3 className="mb-4 font-semibold">Staff activity</h3>
          {d.staff_activity === null ? (
            <p className="text-sm text-muted-foreground">Staff activity visibility is a Pro feature. <Link to="/dashboard/account" className="text-emerald-600">Upgrade</Link></p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground"><tr><th className="pb-2">Staff</th><th>Total</th><th>Notes</th><th>Stage moves</th><th>Follow-ups</th></tr></thead>
              <tbody className="divide-y divide-border font-mono">
                {d.staff_activity.map((s: any) => <tr key={s.name}><td className="py-2 font-sans">{s.name}</td><td>{s.total}</td><td>{s.notes}</td><td>{s.stage_changes}</td><td>{s.followups}</td></tr>)}
              </tbody>
            </table>
          )}
        </Panel>
      </div>
    </div>
  );
}
